import { districts } from '../data/districts';
import { mapLayers } from '../data/mapLayers';
/** districts 配列を id → district の Map に変換（O(1) ルックアップ） */
const districtsMap = new Map(districts.map((d) => [d.id, d]));
const MAP_WIDTH = 2178;
const MAP_HEIGHT = 1362;
const SCROLL_MARGIN = 80;
const SCROLL_MARGIN_Y_SP = 350; // SP: ロゴ＋吹き出し表示分の上下余裕
const MAX_SCALE = 2.1;
/**
 * SP でエリアタップ時のズーム基準幅（論理ピクセル）。
 * この幅がビューポート幅いっぱいに表示されるスケールに拡大する。
 * 値を小さくするほどズームイン量が大きくなる。
 */
const SP_LANDMARK_MAP_WIDTH = 706; // 494 / 0.7 ≈ 706（タップ時ズーム量を約30%削減）
/**
 * map-inner 座標系における全コンテンツの幅・高さ（SP ズーム計算に使用）。
 */
const CONTENT_WIDTH = 1935;
const CONTENT_HEIGHT = 1055;
/**
 * map-inner 座標系における土台の中心点（ground-base の left + width/2, top + height/2）。
 */
const CONTENT_CENTER_X = 1150;
const CONTENT_CENTER_Y = 685;
/**
 * ホイールズームの速度係数。
 * 値を小さくするとズームがゆっくりになる。
 */
const WHEEL_ZOOM_SPEED = 0.0006;
/** ホイールズームの慣性（フレームごとの速度減衰率）*/
const WHEEL_DECELERATION = 0.82;
function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
export class MapView {
    constructor(container, onDistrictClick, onMapClick) {
        Object.defineProperty(this, "viewport", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "mapInner", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "offset", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { x: 0, y: 0 }
        });
        Object.defineProperty(this, "scale", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1.0
        });
        // ── ドラッグ状態 ───────────────────────────────────────────
        Object.defineProperty(this, "isDragging", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "hasDragMoved", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "dragStartPoint", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { x: 0, y: 0 }
        });
        Object.defineProperty(this, "dragStartOffset", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { x: 0, y: 0 }
        });
        /**
         * setPointerCapture 後は e.target がビューポート要素に変わるため、
         * pointerdown 時点のターゲットを保持しておく
         */
        // ── インタラクション抑制フラグ（ナビパネル等が開いている間 true）──
        Object.defineProperty(this, "suppressInteraction", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // ── ピンチズーム状態 ─────────────────────────────────────
        Object.defineProperty(this, "activePointers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "lastPinchDistance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        // ── リサイズ追跡 ──────────────────────────────────────────
        /**
         * リサイズ前のビューポートサイズを保持する。
         * リサイズ時に画面中心のマップ座標を維持するために使用する。
         */
        Object.defineProperty(this, "previousViewportWidth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "previousViewportHeight", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        // ── ホイールズーム慣性 ────────────────────────────────────
        Object.defineProperty(this, "wheelZoomVelocity", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "wheelPivot", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { x: 0, y: 0 }
        });
        Object.defineProperty(this, "wheelAnimationFrameId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // ── スムーズパンアニメーション ────────────────────────────
        Object.defineProperty(this, "panAnimationFrameId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // ── オープニング closeDown アニメーション（SP のみ実行）────
        Object.defineProperty(this, "closeDownAnimationFrameId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // ── ホバー状態管理 ────────────────────────────────────────
        /** 現在ホバー中の districtId（同一区の全レイヤーへ filter を伝播するために使用）*/
        Object.defineProperty(this, "hoveredDistrictId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        /** districtId → 所属する DOM 要素の配列（ホバー時の querySelectorAll を回避） */
        Object.defineProperty(this, "districtElementsCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        /** districtId → グループ div（ホバー時の drop-shadow をグループ単位で適用） */
        Object.defineProperty(this, "districtGroupCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        // ── ピクセル精度ヒットテスト ───────────────────────────────
        /**
         * レイヤーキーごとの PNG ピクセルデータ（RGBA 配列）。
         * 地面レイヤー (hitTestDistrictId を持つ) のデータをここに格納し、
         * 正確な地面領域クリック判定に使用する。
         */
        Object.defineProperty(this, "layerPixelData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "onDistrictClick", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onMapClick", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** transform 変化時（パン・ズーム・アニメーション）に呼ばれるコールバック */
        Object.defineProperty(this, "onTransformChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        /** ユーザーのドラッグ操作開始時に呼ばれるコールバック */
        Object.defineProperty(this, "onPanStart", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        /**
         * mousemove で地面レイヤーのピクセル値を確認してホバー区を特定する。
         * DOM の mouseenter/mouseleave ではなくマップ座標ベースで判定するため、
         * 透明部分（区境界外）を正確に除外できる。
         */
        /** mousemove スロットル用タイムスタンプ */
        Object.defineProperty(this, "_lastMouseMoveTime", {
            enumerable: false, configurable: true, writable: true, value: 0
        });
        /** viewport の getBoundingClientRect キャッシュ（resize/scroll で更新） */
        Object.defineProperty(this, "_viewportRectCache", {
            enumerable: false, configurable: true, writable: true, value: null
        });
        Object.defineProperty(this, "onMouseMoveViewport", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                // ローディング中・ドラッグ中・パンアニメ中・ホイールズーム中はホバー判定しない
                if (document.body.classList.contains('is-loading') || this.isDragging || this.hasDragMoved || this.panAnimationFrameId !== null || this.wheelAnimationFrameId !== null)
                    return;
                // 30ms スロットル（~33fps で十分なホバー応答性）
                const now = performance.now();
                if (now - this._lastMouseMoveTime < 30) return;
                this._lastMouseMoveTime = now;
                // getBoundingClientRect をキャッシュ（resize で無効化）
                if (!this._viewportRectCache) {
                    this._viewportRectCache = this.viewport.getBoundingClientRect();
                }
                const viewportRect = this._viewportRectCache;
                const mapX = (event.clientX - viewportRect.left - this.offset.x) / this.scale;
                const mapY = (event.clientY - viewportRect.top - this.offset.y) / this.scale;
                const hoveredDistrict = this.getDistrictAtMapPosition(mapX, mapY);
                const districtId = hoveredDistrict?.id ?? null;
                if (districtId === this.hoveredDistrictId)
                    return;
                this.clearDistrictHover();
                this.hoveredDistrictId = districtId;
                if (districtId) {
                    const els = this.districtElementsCache.get(districtId);
                    if (els) els.forEach((el) => el.classList.add('map-district--hovered'));
                    this.viewport.style.cursor = 'pointer';
                }
                else {
                    this.viewport.style.cursor = '';
                }
            }
        });
        /** ビューポート外へマウスが出たときにホバーをクリアする */
        Object.defineProperty(this, "onMouseLeaveViewport", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.clearDistrictHover();
                this.viewport.style.cursor = '';
            }
        });
        // ── ポインター / ドラッグ / ピンチ ──────────────────────
        Object.defineProperty(this, "onPointerDown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                if (this.suppressInteraction) return;
                const cx = event.clientX, cy = event.clientY;
                const inPopupLayer = (x, y) => document.elementFromPoint(x, y)?.closest('#popup-layer');
                // ピンの直上をクリック: キャプチャを開始しない
                if (inPopupLayer(cx, cy)) return;
                // POPUP_LIFT ギャップ対策: 8px 上にピンがあればドラッグ抑制モードで続行
                this._pinClickMode = !!(inPopupLayer(cx, cy - 8));
                this.activePointers.set(event.pointerId, event);
                this.viewport.setPointerCapture(event.pointerId);
                if (this.activePointers.size === 1) {
                    this.isDragging = true;
                    this.hasDragMoved = false;
                    this.dragStartPoint = { x: event.clientX, y: event.clientY };
                    this.dragStartOffset = { ...this.offset };
                }
                else if (this.activePointers.size === 2) {
                    this.isDragging = false;
                    this.lastPinchDistance = this.getPinchDistance();
                }
            }
        });
        Object.defineProperty(this, "onPointerMove", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                this.activePointers.set(event.pointerId, event);
                if (this.activePointers.size === 2) {
                    const currentPinchDistance = this.getPinchDistance();
                    if (this.lastPinchDistance > 0) {
                        const zoomFactor = currentPinchDistance / this.lastPinchDistance;
                        const midpoint = this.getPinchMidpoint();
                        this.applyZoom(zoomFactor, midpoint.x, midpoint.y);
                    }
                    this.lastPinchDistance = currentPinchDistance;
                    return;
                }
                if (!this.isDragging)
                    return;
                const deltaX = event.clientX - this.dragStartPoint.x;
                const deltaY = event.clientY - this.dragStartPoint.y;
                if (!this.hasDragMoved && Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10)
                    return;
                // ピン付近クリックモード: マウスが動いても hasDragMoved にせずドラッグを抑制
                if (this._pinClickMode) return;
                if (!this.hasDragMoved) {
                    this.hasDragMoved = true;
                    this.onPanStart?.();
                }
                this.offset.x = this.dragStartOffset.x + deltaX;
                this.offset.y = this.dragStartOffset.y + deltaY;
                this.clampOffset();
                this.applyTransform();
            }
        });
        Object.defineProperty(this, "onPointerUp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                this.activePointers.delete(event.pointerId);
                this.lastPinchDistance = 0;
                if (this.activePointers.size < 2 && this.isDragging) {
                    this.isDragging = false;
                    if (!this.hasDragMoved) {
                        // POPUP_LIFT 分のギャップを吸収: クリック位置の真上もサンプリングして
                        // ピンのボックス外から届いたクリックをピンに転送する
                        const cx = event.clientX, cy = event.clientY;
                        const pin = document.elementFromPoint(cx, cy)?.closest('.popup__pin')
                            ?? document.elementFromPoint(cx, cy - 8)?.closest('.popup__pin');
                        if (pin) {
                            pin.dispatchEvent(new PointerEvent('pointerup', {
                                bubbles: true, cancelable: true,
                                pointerId: event.pointerId,
                                clientX: cx, clientY: cy,
                                pointerType: event.pointerType,
                            }));
                        } else {
                            this.handleTap(event);
                        }
                        // タップ後のゴーストクリック（SP: 300ms 遅延 click がモーダルリンクに届く）を防ぐ
                        event.preventDefault();
                    }
                }
                if (this.activePointers.size === 0) {
                    this.isDragging = false;
                    this.hasDragMoved = false;
                    this._pinClickMode = false;
                    this.invalidateViewportRectCache();
                }
            }
        });
        // ── ホイールズーム（PC・慣性あり）────────────────────────
        Object.defineProperty(this, "onWheel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                if (this.suppressInteraction) return;
                event.preventDefault();
                const viewportRect = this.viewport.getBoundingClientRect();
                this.wheelPivot.x = event.clientX - viewportRect.left;
                this.wheelPivot.y = event.clientY - viewportRect.top;
                // deltaY を速度として累積する（負 = ズームイン、正 = ズームアウト）
                this.wheelZoomVelocity -= event.deltaY * WHEEL_ZOOM_SPEED;
                if (this.wheelAnimationFrameId === null) {
                    this.wheelAnimationFrameId = requestAnimationFrame(this.animateWheelZoom);
                }
            }
        });
        /**
         * 慣性付きホイールズームのアニメーションループ。
         * 速度を毎フレーム減衰させ、十分小さくなったら停止する。
         */
        Object.defineProperty(this, "animateWheelZoom", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (Math.abs(this.wheelZoomVelocity) < 0.0002) {
                    this.wheelZoomVelocity = 0;
                    this.wheelAnimationFrameId = null;
                    return;
                }
                // velocity をズーム倍率に変換: 正の velocity → 拡大
                const zoomFactor = 1 + this.wheelZoomVelocity;
                this.applyZoom(zoomFactor, this.wheelPivot.x, this.wheelPivot.y);
                // 減衰
                this.wheelZoomVelocity *= WHEEL_DECELERATION;
                this.wheelAnimationFrameId = requestAnimationFrame(this.animateWheelZoom);
            }
        });
        // ── リサイズ ──────────────────────────────────────────────
        /**
         * ウインドウリサイズ時に、リサイズ前の画面中心と同じマップ座標が
         * リサイズ後も中心に来るようオフセットを補正する。
         *
         * さまざまなアスペクト比・サイズ変化に対して「飛び」が起きないよう、
         * 旧ビューポートサイズを基準に計算してから clamp する。
         */
        Object.defineProperty(this, "onResize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                // パンアニメーション進行中はキャンセルしてからリサイズ処理を行う
                if (this.panAnimationFrameId !== null) {
                    cancelAnimationFrame(this.panAnimationFrameId);
                    this.panAnimationFrameId = null;
                }
                const oldWidth = this.previousViewportWidth;
                const oldHeight = this.previousViewportHeight;
                const newWidth = this.viewportWidth;
                const newHeight = this.viewportHeight;
                // リサイズ前の画面中心に対応するマップ座標を算出
                const centerMapX = (oldWidth / 2 - this.offset.x) / this.scale;
                const centerMapY = (oldHeight / 2 - this.offset.y) / this.scale;
                // リサイズ後の画面中心に同じマップ座標が来るようにオフセットを調整
                this.offset.x = newWidth / 2 - centerMapX * this.scale;
                this.offset.y = newHeight / 2 - centerMapY * this.scale;
                this.previousViewportWidth = newWidth;
                this.previousViewportHeight = newHeight;
                this.clampOffset();
                this.applyTransform();
                this.invalidateViewportRectCache();
            }
        });
        this.onDistrictClick = onDistrictClick;
        this.onMapClick = onMapClick;
        this.viewport = document.createElement('div');
        this.viewport.id = 'map-viewport';
        this.mapInner = document.createElement('div');
        this.mapInner.id = 'map-inner';
        // 地図全体を1つの画像として扱う。各レイヤー画像は装飾として aria-hidden 化するため、
        // 個別に alt を書かなくてもスクリーンリーダーには地図全体が伝わる。
        this.mapInner.setAttribute('role', 'img');
        this.mapInner.setAttribute('aria-label', '解放美区の地図');
        this.buildAllLayers();
        this.viewport.appendChild(this.mapInner);
        container.appendChild(this.viewport);
        this.initPosition();
        this.applyTransform();
        this.previousViewportWidth = this.viewportWidth;
        this.previousViewportHeight = this.viewportHeight;
        this.bindEvents();
    }
    /** ビューポートの現在の横幅 (px) */
    get viewportWidth() {
        return this.viewport.clientWidth || window.innerWidth;
    }
    /** ビューポートの現在の縦幅 (px) */
    get viewportHeight() {
        return this.viewport.clientHeight || window.innerHeight;
    }
    /**
     * ウインドウサイズに応じた最小スケールを返す。
     * マップの幅・高さ両方がビューポートに収まるスケールを下限とする。
     */
    get minimumScale() {
        // 土台（CONTENT_WIDTH×CONTENT_HEIGHT）の周囲に30pxの余白を確保した最小スケール
        const ZOOM_OUT_PADDING = 30;
        const availW = this.viewportWidth - ZOOM_OUT_PADDING * 2;
        const availH = this.viewportHeight - ZOOM_OUT_PADDING * 2;
        return Math.min(availW / CONTENT_WIDTH, availH / CONTENT_HEIGHT);
    }
    isDesktop() {
        return window.innerWidth >= 1024;
    }
    /** 現在のズーム倍率を返す（バナーのズーム判定に使用） */
    getScale() {
        return this.scale;
    }
    /**
     * 初期表示位置とスケールを設定する。
     * - PC: マップ全体をビューポートに収める
     * - SP: scale=1 で区クラスター中心を画面中央に配置
     */
    initPosition() {
        // ヘッダーがオーバーレイで被るため、見た目の中心はヘッダー分だけ下にずれる
        // その分 Y を上に補正する
        const header = document.getElementById('app-header');
        const headerOffset = header ? header.offsetHeight / 2 : 0;

        if (this.isDesktop()) {
            // minimumScale の 1.25 倍 = マップが画面から少しはみ出す初期倍率
            const fittedScale = Math.min(this.viewportWidth / MAP_WIDTH, this.viewportHeight / MAP_HEIGHT) * 1.15;
            this.scale = clamp(fittedScale, this.minimumScale, MAX_SCALE);
            // 土台の中心をビューポートの視覚的中心に配置する
            this.offset.x = this.viewportWidth / 2 - CONTENT_CENTER_X * this.scale;
            this.offset.y = (this.viewportHeight + headerOffset) / 2 - CONTENT_CENTER_Y * this.scale;
            this.clampOffset();
        }
        else {
            // SP: クローズアップ状態（3.0倍）で開始。出現アニメ後に1.2倍へクローズダウン。
            const contentFitScale = Math.min(this.viewportWidth / CONTENT_WIDTH, this.viewportHeight / CONTENT_HEIGHT) * 3.0;
            this.scale = Math.max(contentFitScale, this.minimumScale);
            // ロゴの下端〜ビューポート下端の中間を地図の中心とする
            const logo = document.getElementById('logo');
            const logoBottom = logo ? logo.getBoundingClientRect().bottom : 0;
            const availableTop = logoBottom;
            const availableHeight = this.viewportHeight - availableTop;
            const visualCenterY = availableTop + availableHeight / 2;
            this.offset.x = this.viewportWidth / 2 - CONTENT_CENTER_X * this.scale;
            this.offset.y = visualCenterY - CONTENT_CENTER_Y * this.scale;
            this.clampOffset();
        }
    }
    /**
     * SP: クローズアップ状態から全体が見えるスケールへゆっくりズームアウトする。
     */
    animateCloseDown() {
        // 全体が見える+10%までズームアウト
        const targetScale = Math.max(
            Math.min(this.viewportWidth / CONTENT_WIDTH, this.viewportHeight / CONTENT_HEIGHT) * 1.2,
            this.minimumScale
        );
        const startScale = this.scale;
        const startOffset = { ...this.offset };
        // ズームアウト先のオフセット（ロゴ下端〜ビューポート下端の中間に土台中心を配置）
        const logo = document.getElementById('logo');
        const logoBottom = logo ? logo.getBoundingClientRect().bottom : 0;
        const visualCenterY = logoBottom + (this.viewportHeight - logoBottom) / 2;
        const targetOffsetX = this.viewportWidth / 2 - CONTENT_CENTER_X * targetScale;
        const targetOffsetY = visualCenterY - CONTENT_CENTER_Y * targetScale;
        // clamp 適用
        const prevScale = this.scale;
        const prevOffset = { ...this.offset };
        this.scale = targetScale;
        this.offset.x = targetOffsetX;
        this.offset.y = targetOffsetY;
        this.clampOffset();
        const clampedOffset = { ...this.offset };
        this.scale = prevScale;
        this.offset = prevOffset;

        const startTime = performance.now();
        const DURATION_MS = 3000; // ゆっくり3秒かけてズームアウト
        const animate = (time) => {
            const elapsed = time - startTime;
            const progress = Math.min(elapsed / DURATION_MS, 1);
            // ease-in-out: ゆったりとした開始と終了
            const eased = progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;
            this.scale = startScale + (targetScale - startScale) * eased;
            this.offset.x = startOffset.x + (clampedOffset.x - startOffset.x) * eased;
            this.offset.y = startOffset.y + (clampedOffset.y - startOffset.y) * eased;
            this.applyTransform();
            if (progress < 1) {
                this.closeDownAnimationFrameId = requestAnimationFrame(animate);
            } else {
                this.closeDownAnimationFrameId = null;
            }
        };
        this.closeDownAnimationFrameId = requestAnimationFrame(animate);
    }
    scaledMapWidth() {
        return MAP_WIDTH * this.scale;
    }
    scaledMapHeight() {
        return MAP_HEIGHT * this.scale;
    }
    /**
     * マップのオフセットを制限する。
     *
     * - マップがビューポートより大きい場合: 端から SCROLL_MARGIN px の余白を保つ
     * - マップがビューポートより小さい場合: overflow 範囲から center 範囲へ線形補間
     *
     * 【ジャンプ防止の設計】
     * コンテンツが viewport に収まる境界（scaledContentW = viewportWidth）で、
     * overflow 範囲と center 範囲を線形補間し、有効範囲が連続的に変化するようにする。
     * t = 0（境界）→ overflow 範囲、t = 1（最小スケール）→ center 範囲。
     */
    clampOffset() {
        const sw = this.scaledMapWidth();
        const sh = this.scaledMapHeight();
        const scw = CONTENT_WIDTH * this.scale;
        const sch = CONTENT_HEIGHT * this.scale;
        const vw = this.viewportWidth;
        const vh = this.viewportHeight;

        // ── X ──
        // overflow 範囲（常に計算）
        const oMinX = -(sw - vw) - SCROLL_MARGIN;
        const oMaxX = SCROLL_MARGIN;

        if (scw > vw) {
            this.offset.x = clamp(this.offset.x, oMinX, oMaxX);
        }
        else {
            // コンテンツが viewport に収まる: overflow → center へ滑らかに補間
            const centerX = vw / 2 - CONTENT_CENTER_X * this.scale;
            const cMinX = centerX - SCROLL_MARGIN;
            const cMaxX = centerX + SCROLL_MARGIN;
            // t: 0（境界）→ 1（コンテンツが viewport の半分以下）
            const t = clamp(1 - scw / vw, 0, 1);
            const minX = oMinX + (cMinX - oMinX) * t;
            const maxX = oMaxX + (cMaxX - oMaxX) * t;
            this.offset.x = clamp(this.offset.x, minX, maxX);
        }

        // ── Y ──
        const marginY = this.isDesktop() ? SCROLL_MARGIN : SCROLL_MARGIN_Y_SP;
        const oMinY = -(sh - vh) - marginY;
        const oMaxY = marginY;

        if (sch > vh) {
            this.offset.y = clamp(this.offset.y, oMinY, oMaxY);
        }
        else {
            const centerY = vh / 2 - CONTENT_CENTER_Y * this.scale;
            const cMinY = centerY - marginY;
            const cMaxY = centerY + marginY;
            const t = clamp(1 - sch / vh, 0, 1);
            const minY = oMinY + (cMinY - oMinY) * t;
            const maxY = oMaxY + (cMaxY - oMaxY) * t;
            this.offset.y = clamp(this.offset.y, minY, maxY);
        }
    }
    applyTransform() {
        this.mapInner.style.transform =
            `translate(${this.offset.x}px, ${this.offset.y}px) scale(${this.scale})`;
        this.onTransformChange?.();
    }
    /** viewport rect キャッシュを明示的に無効化する（resize / scroll 時に呼ぶ） */
    invalidateViewportRectCache() {
        this._viewportRectCache = null;
    }
    /**
     * mapLayers の定義に基づき、全レイヤーを map-inner に追加する。
     * Figma のレイヤー順（配列順 = 下から上）で z-index が決まる。
     *
     * 区画レイヤーは pointer-events: auto （クリック可能）、
     * デコレーションレイヤーは pointer-events: none （非インタラクティブ）。
     *
     * 区画の画像はピクセルデータを事前ロードし、
     * 後のヒットテストに使用する。
     *
     * ページロード時のアニメーション:
     *   base-*  : 0ms から 80ms ずつずらして土台を出現
     *   district-*: 1区 → 大型建物順で 600ms から出現（個別遅延）
     *   deco-*  : 1400ms から 35ms ずつずらして装飾を出現
     */
    buildAllLayers() {
        // 土台レイヤーの出現順（1区 → 7区、道路は最後）
        const BASE_DELAYS = {
            'base/bridge-shadow-4ku': 900,
            'base/bridge-shadow-5ku': 900,
            'base/ground-base': 0,
            'base/ground-bricks': 0,
            'base/hit-ground-1ku': 0,
            'base/hit-ground-2ku': 40,
            'base/hit-ground-3ku': 80,
            'base/hit-ground-4ku': 120,
            'base/hit-ground-5ku': 160,
            'base/hit-ground-6ku': 200,
            'base/hit-ground-7ku': 240,
        };
        // ランドマーク建物の出現順（1区 → 7区の順番で順番に出現）
        // キーが完全一致 → fallback のプレフィックス照合（district-Nku）の順で参照される
        const DISTRICT_DELAYS = {
            // ─── プレフィックス fallback（キー districts/Nku/... は prefix districts/Nku でマッチ）───
            'districts/1ku': 300,
            'districts/2ku': 420,
            'districts/3ku': 540,
            'districts/4ku': 640,
            'districts/5ku': 740,
            'districts/6ku': 840,
            'districts/7ku': 940,
            // ─── 橋（影と同時に出現）────────────────────────────────
            'districts/4ku/bridge': 900,
            'districts/5ku/bridge': 900,
            'clouds/clouds': 1500,
            'clouds/cloud-shadows': 1500,
        };
        let decoCounter = 0;
        /** 区ごとにレイヤー出現の連続スタッガーを積み上げるカウンター */
        const districtPartCounters = new Map();
        /** 区内パーツ1つあたりの出現遅延（ms）。長くするほど連続感が増す。*/
        const STAGGER_PER_PART = 30;
        // ── ホバー時スタッガー遅延の事前計算 ───────────────────────
        // 大きな建物を先に、同サイズならランドマーク（pinPoint）に近い方から浮く
        // shadowOf を持つレイヤーは本体と同時に動かすため、計算から除外して後で同期する
        const hoverDelayMap = new Map();
        {
            const grouped = new Map();
            const shadowLayers = []; // shadowOf 付きレイヤーを一旦退避
            for (const layer of mapLayers) {
                if (!layer.districtId)
                    continue;
                // 影レイヤーは本体と同期するため個別に集める
                if (layer.shadowOf) {
                    shadowLayers.push(layer);
                    continue;
                }
                const district = districtsMap.get(layer.districtId);
                if (!district)
                    continue;
                const area = layer.width * layer.height;
                const pinX = district.pinPoint?.x ?? district.left + district.width / 2;
                const pinY = district.pinPoint?.y ?? district.top + district.height / 2;
                const cx = layer.left + layer.width / 2;
                const cy = layer.top + layer.height / 2;
                const dist = Math.hypot(cx - pinX, cy - pinY);
                if (!grouped.has(layer.districtId))
                    grouped.set(layer.districtId, []);
                grouped.get(layer.districtId).push({ key: layer.key, area, dist });
            }
            const HOVER_STAGGER_TOTAL_MS = 300; // 全区で演出の総時間を統一
            for (const [, layers] of grouped) {
                // 面積降順 → 距離昇順（大きいパーツ・ランドマークに近いパーツが先）
                layers.sort((a, b) => b.area - a.area || a.dist - b.dist);
                // パーツ数に応じて間隔を自動調整（少ないパーツでも十分な差が出る）
                const step = layers.length > 1 ? HOVER_STAGGER_TOTAL_MS / (layers.length - 1) : 0;
                layers.forEach((l, idx) => hoverDelayMap.set(l.key, Math.round(idx * step)));
            }
            // 影レイヤーに対応する本体と同じ delay を割り当てる
            for (const shadow of shadowLayers) {
                const bodyDelay = hoverDelayMap.get(shadow.shadowOf) ?? 0;
                hoverDelayMap.set(shadow.key, bodyDelay);
            }
        }
        // ── グループ div を DOM 順（背面→前面）で事前生成 ───────────
        const GROUP_ORDER = [
            'base',
            'cloud-shadows',
            'district-3ku', 'district-4ku', 'district-1ku', 'district-2ku',
            'district-6ku', 'district-7ku', 'district-5ku',
            'clouds',
        ];
        const groupElements = new Map();
        for (const groupId of GROUP_ORDER) {
            const div = document.createElement('div');
            div.className = 'map-group';
            div.dataset.group = groupId;
            groupElements.set(groupId, div);
            this.mapInner.appendChild(div);
            // district-Xku グループの div を districtId でキャッシュ
            const districtMatch = groupId.match(/^district-(\d+ku)$/);
            if (districtMatch) {
                this.districtGroupCache.set(districtMatch[1], div);
            }
        }
        /** レイヤーキーから所属グループ ID を返す */
        /** 描画グループを上書きするマップ（重なり順調整用） */
        const groupOverrides = {
            'districts/5ku/bridge': 'district-6ku',
        };
        const getGroupId = (key) => {
            if (groupOverrides[key])
                return groupOverrides[key];
            if (key.startsWith('base/'))
                return 'base';
            if (key.startsWith('clouds/shadow'))
                return 'cloud-shadows';
            if (key.startsWith('clouds/'))
                return 'clouds';
            const m = key.match(/^districts\/(\d+ku)/);
            if (m)
                return `district-${m[1]}`;
            return 'deco'; // 未分類は deco 扱い
        };
        for (const layer of mapLayers) {
            const imageElement = document.createElement('img');
            imageElement.src = layer.imageSrc;
            imageElement.alt = '';
            imageElement.setAttribute('aria-hidden', 'true');
            imageElement.draggable = false;
            imageElement.decoding = 'async';
            // 2× PNG を論理座標サイズで表示する（CSS クラスでは幅高さを指定できないため inline で設定）
            imageElement.style.width = `${layer.width}px`;
            imageElement.style.height = `${layer.height}px`;
            imageElement.style.left = `${layer.left}px`;
            imageElement.style.top = `${layer.top}px`;
            // 影レイヤー等のブレンドモード（multiply など）を適用する
            if (layer.blendMode) {
                imageElement.style.mixBlendMode = layer.blendMode;
                // Safari 対策: multiply レイヤーは GPU 合成レイヤーを常時維持するクラスを付与
                // (.map-layer--multiply { will-change: transform } → 他要素の filter 変化に影響されない)
                if (layer.blendMode === 'multiply') {
                    imageElement.classList.add('map-layer--multiply');
                }
            }
            // 明示的な opacity 指定（CSS変数でアニメーション終端値を制御）
            if (layer.opacity !== undefined) {
                imageElement.style.setProperty('--layer-opacity', String(layer.opacity));
            }
            if (layer.districtId) {
                imageElement.className = 'map-layer map-district' + (layer.blendMode === 'multiply' ? ' map-layer--multiply' : '');
                imageElement.dataset.districtId = layer.districtId;
                imageElement.dataset.layerKey = layer.key;
                // ホバー用要素キャッシュに登録（querySelectorAll を回避）
                const cached = this.districtElementsCache.get(layer.districtId);
                if (cached) cached.push(imageElement);
                else this.districtElementsCache.set(layer.districtId, [imageElement]);
                // ホバー時のテーマ色シャドウ用 CSS 変数を設定する
                // ホバー時のテーマ色シャドウ用 CSS 変数（multiply レイヤーはスキップ）
                if (!layer.blendMode) {
                    const district = districtsMap.get(layer.districtId);
                    if (district) {
                        const hex = district.color;
                        const r = parseInt(hex.slice(1, 3), 16);
                        const g = parseInt(hex.slice(3, 5), 16);
                        const b = parseInt(hex.slice(5, 7), 16);
                        imageElement.style.setProperty('--district-hover-shadow', `rgba(${r}, ${g}, ${b}, 0.35)`);
                    }
                }
                // ホバー時のスタッガー遅延（面積大→小、ランドマーク近→遠の順）
                const hoverDelay = hoverDelayMap.get(layer.key) ?? 0;
                imageElement.style.setProperty('--hover-delay', `${hoverDelay}ms`);
            }
            else {
                imageElement.className = 'map-layer';
            }
            // ロードアニメーション: グループ別にずらしてドロップイン
            let delayMs;
            if (layer.key in BASE_DELAYS) {
                // 土台レイヤー: 区番号順に出現
                delayMs = BASE_DELAYS[layer.key];
            }
            else if (layer.key in DISTRICT_DELAYS) {
                // 区建物レイヤー（完全一致）
                delayMs = DISTRICT_DELAYS[layer.key];
            }
            else {
                // 区サブレイヤー（例: districts/1ku/building-a）: プレフィックスで親区の遅延を継承
                const districtPrefix = layer.key.match(/^(districts\/\d+ku)/)?.[1];
                if (districtPrefix && districtPrefix in DISTRICT_DELAYS) {
                    // 区内のパーツ連続スタッガー：部品ごとに STAGGER_PER_PART ms ずつ遅延を積み上げる
                    // 影レイヤー（fade / shadowOf あり）はカウンターを消費しない → 直後の本体と同タイミングで出現
                    const partIdx = districtPartCounters.get(districtPrefix) ?? 0;
                    if (layer.animationStyle !== 'fade' && !layer.shadowOf) {
                        districtPartCounters.set(districtPrefix, partIdx + 1);
                    }
                    delayMs = DISTRICT_DELAYS[districtPrefix] + partIdx * STAGGER_PER_PART;
                }
                else if (layer.districtId && `districts/${layer.districtId}` in DISTRICT_DELAYS) {
                    // deco/* レイヤーは districtId から親区の遅延を継承する
                    const dPrefix = `districts/${layer.districtId}`;
                    const partIdx = districtPartCounters.get(dPrefix) ?? 0;
                    if (layer.animationStyle !== 'fade' && !layer.shadowOf) {
                        districtPartCounters.set(dPrefix, partIdx + 1);
                    }
                    delayMs = DISTRICT_DELAYS[dPrefix] + partIdx * STAGGER_PER_PART;
                }
                else {
                    delayMs = 1600 + decoCounter * 35;
                    decoCounter++;
                }
            }
            // 静的レイヤー（橋の影など）はアニメーション・ホバー対象外。常に表示。
            if (layer.static) {
                imageElement.dataset.static = 'true';
                const groupId = getGroupId(layer.key);
                (groupElements.get(groupId) ?? this.mapInner).appendChild(imageElement);
                continue;
            }
            // ヒットテスト専用レイヤーはアニメーションを付与しない。
            // CSS アニメーション（forwards fill の opacity:1）がインラインの opacity:0 を
            // 上書きしてしまうため、hitTestDistrictId レイヤーには別途非表示処理のみ行う。
            if (layer.hitTestDistrictId) {
                imageElement.style.opacity = '0';
                imageElement.style.pointerEvents = 'none';
                this.loadLayerPixelData(layer.key, imageElement);
            }
            else if (layer.animationStyle === 'fade') {
                imageElement.classList.add('map-layer--animate-fade');
                imageElement.style.animationDelay = `${delayMs}ms`;
                imageElement.addEventListener('animationend', () => {
                    imageElement.classList.remove('map-layer--animate-fade');
                    // CSS で別のアニメーションが定義されている場合は復元、そうでなければ停止
                    imageElement.style.animation = '';
                    const restored = getComputedStyle(imageElement).animationName;
                    if (restored === 'none' || !restored) {
                        imageElement.style.animation = 'none';
                    }
                    // multiply レイヤーは inline opacity を設定しない（ホバー時 CSS opacity:0 制御を維持）
                    if (!layer.blendMode) {
                        imageElement.style.opacity = String(layer.opacity ?? 1);
                    }
                }, { once: true });
            }
            else if (layer.animationStyle === 'cloud') {
                imageElement.classList.add('map-layer--animate-cloud');
                imageElement.style.animationDelay = `${delayMs}ms`;
                // エントリ完了後、ゆっくりとしたふわふわフロートループへ切り替え
                const floatDurationS = (6 + Math.random() * 3).toFixed(1);
                const floatOffsetS = (Math.random() * 6).toFixed(1);
                imageElement.addEventListener('animationend', () => {
                    imageElement.classList.remove('map-layer--animate-cloud');
                    imageElement.style.setProperty('--cloud-float-duration', `${floatDurationS}s`);
                    imageElement.style.setProperty('--cloud-float-offset', `-${floatOffsetS}s`);
                    imageElement.classList.add('map-layer--cloud-float');
                }, { once: true });
            }
            else {
                imageElement.classList.add('map-layer--animate');
                imageElement.style.animationDelay = `${delayMs}ms`;
                imageElement.addEventListener('animationend', () => {
                    imageElement.classList.remove('map-layer--animate');
                    // CSS で別のアニメーションが定義されている場合は復元、そうでなければ停止
                    imageElement.style.animation = '';
                    const restored = getComputedStyle(imageElement).animationName;
                    if (restored === 'none' || !restored) {
                        imageElement.style.animation = 'none';
                    }
                }, { once: true });
            }
            const groupId = getGroupId(layer.key);
            const parentEl = groupElements.get(groupId) ?? this.mapInner;
            // clipTo: 固定矩形でクリップするラッパーdivに入れる
            // maskSrc がある場合はポール形状でマスクする
            if (layer.clipTo) {
                const wrapper = document.createElement('div');
                wrapper.style.position = 'absolute';
                wrapper.style.left = `${layer.clipTo.left}px`;
                wrapper.style.top = `${layer.clipTo.top}px`;
                wrapper.style.width = `${layer.clipTo.width}px`;
                wrapper.style.height = `${layer.clipTo.height}px`;
                wrapper.style.overflow = 'hidden';
                wrapper.style.pointerEvents = 'none';
                if (layer.clipTo.maskSrc) {
                    wrapper.style.maskImage = `url(${layer.clipTo.maskSrc})`;
                    wrapper.style.webkitMaskImage = `url(${layer.clipTo.maskSrc})`;
                    wrapper.style.maskSize = '100% 100%';
                    wrapper.style.webkitMaskSize = '100% 100%';
                }
                // 画像の位置をラッパー内に相対調整
                imageElement.style.left = `${layer.left - layer.clipTo.left}px`;
                imageElement.style.top = `${layer.top - layer.clipTo.top}px`;
                wrapper.appendChild(imageElement);
                parentEl.appendChild(wrapper);
            } else {
                parentEl.appendChild(imageElement);
            }
        }
    }
    // ── ピクセル精度ヒットテスト ───────────────────────────────
    /**
     * 指定した画像のピクセル RGBA データをキャンバスに描画してキャッシュする。
     * 地面レイヤー (hitTestDistrictId を持つ) に対して呼び出し、
     * 地面領域のピクセル精度クリック判定に使用する。
     */
    loadLayerPixelData(layerKey, imageElement) {
        const extractPixelData = () => {
            if (imageElement.naturalWidth === 0)
                return;
            try {
                const canvas = document.createElement('canvas');
                canvas.width = imageElement.naturalWidth;
                canvas.height = imageElement.naturalHeight;
                const context = canvas.getContext('2d');
                context.drawImage(imageElement, 0, 0);
                const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
                this.layerPixelData.set(layerKey, {
                    data: imageData.data,
                    canvasWidth: canvas.width,
                    canvasHeight: canvas.height,
                });
            }
            catch {
                // CORS 等の理由でピクセルデータを取得できない場合はバウンディングボックスにフォールバック
            }
        };
        if (imageElement.complete && imageElement.naturalWidth > 0) {
            extractPixelData();
        }
        else {
            imageElement.addEventListener('load', extractPixelData, { once: true });
        }
    }
    /**
     * 指定したマップ座標に区画が存在するかを確認する。
     *
     * 判定優先順位:
     * 1. 地面レイヤー (hitTestDistrictId) のピクセルで判定
     *    → 建物の重なりを除外し、地面タイルの正確な形状でクリック判定する
     * 2. フォールバック: 建物込みの district 画像レイヤー (districtId) で判定
     *    → 地面レイヤーがヒットしなかった場合（建物上をクリックした場合など）
     */
    getDistrictAtMapPosition(mapX, mapY) {
        // 1. 地面レイヤーで判定（z-index 高い順 = 配列末尾から）
        for (let index = mapLayers.length - 1; index >= 0; index--) {
            const layer = mapLayers[index];
            if (!layer.hitTestDistrictId)
                continue;
            const relX = mapX - layer.left;
            const relY = mapY - layer.top;
            if (relX < 0 || relY < 0 || relX >= layer.width || relY >= layer.height)
                continue;
            const pixelData = this.layerPixelData.get(layer.key);
            if (!pixelData) {
                // データ未ロードの場合はバウンディングボックスにフォールバック
                return districtsMap.get(layer.hitTestDistrictId) ?? null;
            }
            const pixelX = Math.floor((relX / layer.width) * pixelData.canvasWidth);
            const pixelY = Math.floor((relY / layer.height) * pixelData.canvasHeight);
            const alpha = pixelData.data[(pixelY * pixelData.canvasWidth + pixelX) * 4 + 3];
            if (alpha > 10) {
                return districtsMap.get(layer.hitTestDistrictId) ?? null;
            }
        }
        // 2. フォールバック: 建物込みの district 画像レイヤーで判定
        for (let index = mapLayers.length - 1; index >= 0; index--) {
            const layer = mapLayers[index];
            if (!layer.districtId)
                continue;
            const relX = mapX - layer.left;
            const relY = mapY - layer.top;
            if (relX < 0 || relY < 0 || relX >= layer.width || relY >= layer.height)
                continue;
            return districtsMap.get(layer.districtId) ?? null;
        }
        return null;
    }
    // ── イベントバインド ──────────────────────────────────────
    bindEvents() {
        this.viewport.style.touchAction = 'none';
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerUp);
        this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
        // ホバー判定: 地面ピクセルデータを使った精度の高い mousemove ベース検出
        this.viewport.addEventListener('mousemove', this.onMouseMoveViewport);
        this.viewport.addEventListener('mouseleave', this.onMouseLeaveViewport);
        window.addEventListener('resize', this.onResize);
        // ナビパネル開閉でインタラクションを抑制 / 復帰
        this._onMenuOpen = () => { this.suppressInteraction = true; };
        this._onMenuClose = () => { this.suppressInteraction = false; };
        document.addEventListener('menupanel:open', this._onMenuOpen);
        document.addEventListener('menupanel:close', this._onMenuClose);
    }
    /** 全イベントリスナーを解除してリソースを解放する */
    destroy() {
        if (this.panAnimationFrameId !== null) {
            cancelAnimationFrame(this.panAnimationFrameId);
            this.panAnimationFrameId = null;
        }
        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport.removeEventListener('pointermove', this.onPointerMove);
        this.viewport.removeEventListener('pointerup', this.onPointerUp);
        this.viewport.removeEventListener('pointercancel', this.onPointerUp);
        this.viewport.removeEventListener('wheel', this.onWheel);
        this.viewport.removeEventListener('mousemove', this.onMouseMoveViewport);
        this.viewport.removeEventListener('mouseleave', this.onMouseLeaveViewport);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('menupanel:open', this._onMenuOpen);
        document.removeEventListener('menupanel:close', this._onMenuClose);
    }
    clearDistrictHover() {
        if (!this.hoveredDistrictId)
            return;
        const els = this.districtElementsCache.get(this.hoveredDistrictId);
        if (els) els.forEach((el) => el.classList.remove('map-district--hovered'));
        this.hoveredDistrictId = null;
    }
    /**
     * ドラッグなしのタップ処理。
     * ピクセル精度ヒットテストで区画を特定してコールバックを呼び出す。
     */
    handleTap(event) {
        const viewportRect = this.viewport.getBoundingClientRect();
        const mapX = (event.clientX - viewportRect.left - this.offset.x) / this.scale;
        const mapY = (event.clientY - viewportRect.top - this.offset.y) / this.scale;
        const tappedDistrict = this.getDistrictAtMapPosition(mapX, mapY);
        if (tappedDistrict) {
            this.onDistrictClick(tappedDistrict);
        }
        else {
            this.onMapClick();
        }
    }
    // ── ズーム計算 ────────────────────────────────────────────
    /**
     * 指定した画面座標 (pivotX, pivotY) を中心にズームを適用する。
     * ズーム後もその座標がマップ上の同じ点を指すよう、オフセットを補正する。
     */
    applyZoom(zoomFactor, pivotX, pivotY) {
        const newScale = clamp(this.scale * zoomFactor, this.minimumScale, MAX_SCALE);
        const scaleRatio = newScale / this.scale;
        this.offset.x = pivotX - (pivotX - this.offset.x) * scaleRatio;
        this.offset.y = pivotY - (pivotY - this.offset.y) * scaleRatio;
        this.scale = newScale;
        this.clampOffset();
        this.applyTransform();
    }
    /** 2本指の距離 (px) を返す */
    getPinchDistance() {
        const pointerList = [...this.activePointers.values()];
        if (pointerList.length < 2)
            return 0;
        const deltaX = pointerList[0].clientX - pointerList[1].clientX;
        const deltaY = pointerList[0].clientY - pointerList[1].clientY;
        return Math.hypot(deltaX, deltaY);
    }
    /** 2本指の中点をビューポート相対座標で返す */
    getPinchMidpoint() {
        const pointerList = [...this.activePointers.values()];
        const viewportRect = this.viewport.getBoundingClientRect();
        return {
            x: (pointerList[0].clientX + pointerList[1].clientX) / 2 - viewportRect.left,
            y: (pointerList[0].clientY + pointerList[1].clientY) / 2 - viewportRect.top,
        };
    }
    // ── 公開 API ──────────────────────────────────────────────
    /** マップ transform 変化時に呼ぶコールバックを設定する（ポップアップ追従用）*/
    setOnTransformChange(callback) {
        this.onTransformChange = callback;
    }
    /** ユーザーのドラッグ開始時に呼ぶコールバックを設定する（ポップアップ非表示用）*/
    setOnPanStart(callback) {
        this.onPanStart = callback;
    }
    /**
     * 現在のオフセットに対して指定量を短いアニメーションで加算する。
     * 吹き出しが画面外にはみ出した場合の微調整パンに使用する。
     */
    animateOffsetShift(dx, dy) {
        const startOffset = { ...this.offset };
        const targetX = this.offset.x + dx;
        const targetY = this.offset.y + dy;
        const startTime = performance.now();
        const DURATION = 300;
        const animate = (time) => {
            const t = Math.min((time - startTime) / DURATION, 1);
            const ease = 1 - Math.pow(1 - t, 3); // ease-out-cubic
            this.offset.x = startOffset.x + (targetX - startOffset.x) * ease;
            this.offset.y = startOffset.y + (targetY - startOffset.y) * ease;
            this.clampOffset();
            this.applyTransform();
            if (t < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
    }
    /**
     * キーボードフォーカス用のパン。ズームは変えず、指定区画のピンが
     * ビューポート中央付近に来るよう 400ms でスムーズにパンする。
     * all-pins モードでの Tab ナビゲーション時に使用する。
     *
     * @param district - フォーカス先の区画
     */
    panToDistrictKbd(district) {
        // ピン座標（map 座標系）
        const pinX = district.pinPoint?.x ?? district.panPoint?.x ?? district.left + district.width / 2;
        const pinY = district.pinPoint?.y ?? district.panPoint?.y ?? district.top + district.height / 2;
        // ズームは維持したまま、ビューポート 60% 位置にピンが来るようオフセットを計算
        const targetX = this.viewportWidth / 2 - pinX * this.scale;
        const targetY = this.viewportHeight * 0.6 - pinY * this.scale;
        // clamp を通して有効範囲に収める
        const savedOffset = { ...this.offset };
        this.offset.x = targetX;
        this.offset.y = targetY;
        this.clampOffset();
        const clampedX = this.offset.x;
        const clampedY = this.offset.y;
        this.offset = savedOffset;
        const dx = clampedX - this.offset.x;
        const dy = clampedY - this.offset.y;
        // 既にほぼ中央なら不要
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        const startOffset = { ...this.offset };
        const startTime = performance.now();
        const DURATION = 400;
        const animate = (time) => {
            const t = Math.min((time - startTime) / DURATION, 1);
            const ease = 1 - Math.pow(1 - t, 3); // ease-out-cubic
            this.offset.x = startOffset.x + dx * ease;
            this.offset.y = startOffset.y + dy * ease;
            this.clampOffset();
            this.applyTransform();
            if (t < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
    }
    /**
     * 指定した区画の中心がビューポート中央付近に来るよう、
     * ease-out アニメーションを伴ってスムーズにパンする。
     * アニメーション完了後に onComplete を呼び出す。
     *
     * @param district - パン先の区画
     * @param onComplete - パン完了時のコールバック
     */
    panToDistrict(district, onComplete) {
        // 進行中の closeDown / パンアニメーションをキャンセルする
        if (this.closeDownAnimationFrameId !== null) {
            cancelAnimationFrame(this.closeDownAnimationFrameId);
            this.closeDownAnimationFrameId = null;
        }
        if (this.panAnimationFrameId !== null) {
            cancelAnimationFrame(this.panAnimationFrameId);
            this.panAnimationFrameId = null;
        }
        // SP: ランドマーク中心を基準に、画面幅に応じた一定のズーム量を計算する
        // SP_LANDMARK_MAP_WIDTH px 分のマップがビューポート幅いっぱいになるスケール
        const targetScale = !this.isDesktop()
            ? clamp(this.viewportWidth / SP_LANDMARK_MAP_WIDTH, this.minimumScale, MAX_SCALE)
            : this.scale;
        // panPoint → pinPoint → バウンディングボックス中心 の優先順でパン目的地を決定する
        const districtCenterX = district.panPoint?.x ?? district.pinPoint?.x ?? district.left + district.width / 2;
        const districtCenterY = district.panPoint?.y ?? district.pinPoint?.y ?? district.top + district.height / 2;
        // ポップアップが区画の上に表示されるため、ランドマーク中心を画面下寄りに配置する
        // SP: 吹き出しが見切れないよう 65% 位置に（PCは60%）
        const panRatio = this.isDesktop() ? 0.6 : 0.65;
        const targetX = this.viewportWidth / 2 - districtCenterX * targetScale;
        const targetY = this.viewportHeight * panRatio - districtCenterY * targetScale;
        // クランプを適用した最終目標オフセットとスケールを算出する
        const previousOffset = { ...this.offset };
        const previousScale = this.scale;
        this.scale = targetScale;
        this.offset.x = targetX;
        this.offset.y = targetY;
        this.clampOffset();
        const targetOffset = { ...this.offset };
        this.scale = previousScale;
        this.offset = previousOffset;
        const startScale = this.scale;
        const startOffset = { ...this.offset };
        const startTime = performance.now();
        const DURATION_MS = 700;
        const animateFrame = (time) => {
            const elapsed = time - startTime;
            const progress = Math.min(elapsed / DURATION_MS, 1);
            // ease-out-quint: 序盤から素早く動き出し、終盤でなめらかに着地する
            const eased = 1 - Math.pow(1 - progress, 5);
            this.scale = startScale + (targetScale - startScale) * eased;
            this.offset.x = startOffset.x + (targetOffset.x - startOffset.x) * eased;
            this.offset.y = startOffset.y + (targetOffset.y - startOffset.y) * eased;
            this.applyTransform();
            if (progress < 1) {
                this.panAnimationFrameId = requestAnimationFrame(animateFrame);
            }
            else {
                this.panAnimationFrameId = null;
                onComplete();
            }
        };
        this.panAnimationFrameId = requestAnimationFrame(animateFrame);
    }
    /**
     * 指定した区画の中心点をビューポート相対座標で返す。
     * ポップアップの表示位置計算に使用する。
     */
    getDistrictScreenCenter(district) {
        // pinPoint が指定されている場合はランドマーク中心を優先する
        const mapX = district.pinPoint?.x ?? district.left + district.width / 2;
        const mapY = district.pinPoint?.y ?? district.top + district.height / 2;
        return {
            x: this.offset.x + mapX * this.scale,
            y: this.offset.y + mapY * this.scale,
        };
    }
    /**
     * 指定した区画の中心がビューポート内に収まるよう、
     * 必要に応じてマップをスクロールする。
     */
    ensureDistrictVisible(district) {
        const districtScreenCenterX = this.offset.x + (district.left + district.width / 2) * this.scale;
        const districtScreenCenterY = this.offset.y + (district.top + district.height / 2) * this.scale;
        const edgePadding = 120;
        let adjustedOffsetX = this.offset.x;
        let adjustedOffsetY = this.offset.y;
        if (districtScreenCenterX < edgePadding) {
            adjustedOffsetX += edgePadding - districtScreenCenterX;
        }
        if (districtScreenCenterX > this.viewportWidth - edgePadding) {
            adjustedOffsetX -= districtScreenCenterX - (this.viewportWidth - edgePadding);
        }
        if (districtScreenCenterY < edgePadding) {
            adjustedOffsetY += edgePadding - districtScreenCenterY;
        }
        if (districtScreenCenterY > this.viewportHeight - 200) {
            adjustedOffsetY -= districtScreenCenterY - (this.viewportHeight - 200);
        }
        this.offset.x = adjustedOffsetX;
        this.offset.y = adjustedOffsetY;
        this.clampOffset();
        this.applyTransform();
    }
}
